import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { PermissionType, RoleType } from '@/constants/permissions';
import ZeusLoader from './ui/ZeusLoader';
import AccessDenied from '@/pages/AccessDenied';

interface PrivateRouteProps {
  children: ReactNode;
  requiredPermission?: PermissionType | string;
  requiredRole?: RoleType | string;
  adminOnly?: boolean;
  globalAdminOnly?: boolean;
  redirectTo?: string;
}

const PrivateRoute = ({
  children,
  requiredPermission,
  requiredRole,
  adminOnly = false,
  globalAdminOnly = false,
  redirectTo = '/login'
}: PrivateRouteProps) => {
  const {
    isAuthenticated,
    isLoading,
    hasPermission,
    hasRoleLevel,
    isAdmin,
    isGlobalAdmin
  } = useAuth();

  if (isLoading) {
    return <ZeusLoader size="lg" fullPage withBackground />;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  if (globalAdminOnly && !isGlobalAdmin()) {
    return <AccessDenied />;
  }

  if (adminOnly && !isAdmin()) {
    return <AccessDenied />;
  }

  if (requiredRole && !hasRoleLevel(requiredRole)) {
    return <AccessDenied />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <AccessDenied />;
  }

  return children;
};

export default PrivateRoute;
